import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import useUser from '@hooks/user/use-user';
import useUpdatePassword from '@hooks/user/use-update-password';
import AccountSettingsSectionHeader from '@components/account-settings/account-settings-section-header/account-settings-section-header';
import AccountDetailsDisplay from './account-details-display/account-details-display';
import AccountDetailsEdit from './account-details-edit/account-details-edit';

const AccountDetails: React.FC = () => {
  const { t } = useTranslation('account-settings');
  const { t: tCommon } = useTranslation('common');
  const { user } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { mutateAsync: updatePassword, isLoading } = useUpdatePassword(user?.id ?? '');

  if (!user) {
    return null;
  }

  const handleSave = async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
    setApiError(null);
    try {
      await updatePassword({ password: newPassword, oldPassword: currentPassword });
      setIsEditing(false);
    } catch (error) {
      setApiError((error?.response?.data?.errors?.detail as string) ?? tCommon('ALERT.GENERIC_ERROR'));
    }
  };

  return (
    <section>
      <AccountSettingsSectionHeader
        title={t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.ACCOUNT_TITLE')}
        onEditClick={() => {
          setIsEditing((prev) => !prev);
          setApiError(null);
        }}
        isEditing={isEditing}
        trackingProperty="account_details"
        data-cy="account-settings-account-details-edit-button"
      />

      {isEditing ? (
        <AccountDetailsEdit
          email={user.email}
          isPending={isLoading}
          onSave={handleSave}
          apiError={apiError}
          onErrorReset={() => setApiError(null)}
        />
      ) : (
        <AccountDetailsDisplay email={user.email} />
      )}
    </section>
  );
};

export default AccountDetails;
